import { useState } from "react";
import { useTranslation } from "react-i18next";

import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import Grid from "@mui/material/Unstable_Grid2/Grid2";
import RepeatIcon from "@mui/icons-material/Repeat";
import TabPanel, { TabPanelProps } from "@mui/lab/TabPanel";
import {
  Box,
  Fade,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  ListSubheaderProps,
  MenuItem,
  MenuList,
  Paper,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";

import { ExportV2, TargetIcon } from "@robin/common";

import { DateTime } from "./primitives/DateTime";
import { HorizontalStack } from "./primitives/Stacks";
import { Patient } from "../models/patient";
import { useAppContext } from "../context/AppContext";
import { useWeeklyTargetsData } from "../hooks/useWeeklyTargetsData";
import { Badge } from "./primitives/Badge";

const listSubheaderProps: ListSubheaderProps = {
  sx: {
    bgcolor: "background.default",
    borderBottom: "0.75px solid",
    borderColor: "divider",
    color: "text.primary",
    fontWeight: 500,
  },
};

const Wrap = styled(Paper)(() => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "row",
  height: "100%",
  overflow: "hidden",
  position: "relative",
}));
const Side = styled(Box)(() => ({
  flex: "0 0 50%",
}));
const Main = styled(Box)(({ theme }) => ({
  borderLeft: `0.75px solid ${theme.palette.divider}`,
  flex: "0 0 50%",
}));

export const GoalsTab: React.FC<TabPanelProps & { patient: Patient }> = ({
  patient,
  ...tabPanelProps
}) => {
  const { t } = useTranslation();

  const { dataConfig } = useAppContext();

  const [targetId, setTargetId] = useState<ExportV2.WeeklyTarget["id"] | null>(
    null
  );
  const [todosVisible, setTodosVisible] = useState(false);

  const { goals, logs, todo } = useWeeklyTargetsData({
    exportId: dataConfig.exportId || undefined,
    patientId: patient.id,
    targetId,
  });

  if (!goals) return;

  return (
    <TabPanel
      {...tabPanelProps}
      sx={{ overflow: "hidden", position: "relative" }}
    >
      <Wrap variant="outlined">
        <Side>
          <MenuList
            dense
            disablePadding
            sx={{
              maxHeight: "100%",
              overflow: "auto",
              scrollbarWidth: "thin",
            }}
            subheader={
              <ListSubheader {...listSubheaderProps}>
                <HorizontalStack alignItems="center" spacing={1}>
                  <span>{t("tabs.goals.goals-label")}</span>
                  <Badge size="small" variant="outlined" label={goals.length} />
                </HorizontalStack>
              </ListSubheader>
            }
          >
            <MenuItem
              selected={targetId === null}
              onClick={() => {
                setTargetId(null);
                setTodosVisible(false);
              }}
            >
              <ListItemIcon>
                <TargetIcon fontSize="small" sx={{ color: "text.disabled" }} />
              </ListItemIcon>
              <ListItemText sx={{ flexGrow: 1 }}>
                {t("tabs.goals.goals-all")}
              </ListItemText>
            </MenuItem>
            {goals.map((goal) => (
              <MenuItem
                key={goal.id}
                onClick={() => {
                  setTargetId(goal.id);
                  setTodosVisible(false);
                }}
                selected={goal.id === targetId}
                sx={{ opacity: goal.active ? 1 : 0.5 }}
              >
                <ListItemIcon>
                  <TargetIcon
                    fontSize="small"
                    sx={{ color: "text.disabled" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={goal.title}
                  primaryTypographyProps={{ noWrap: true }}
                />
                <HorizontalStack alignItems="center" spacing={1.5}>
                  {goal.repeat && (
                    <Tooltip title={t("tabs.goals.goals-repating-tip")}>
                      <RepeatIcon
                        fontSize="inherit"
                        sx={{ color: "text.disabled" }}
                      />
                    </Tooltip>
                  )}
                  <Tooltip
                    title={t("tabs.goals.goals-count-tip", {
                      count: goal.count,
                    })}
                  >
                    <Typography color="text.disabled" variant="caption">
                      × {goal.count}
                    </Typography>
                  </Tooltip>
                </HorizontalStack>
              </MenuItem>
            ))}
          </MenuList>
        </Side>
        <Main>
          <List
            dense
            sx={{
              maxHeight: "100%",
              overflow: "auto",
              scrollbarWidth: "thin",
            }}
            subheader={
              <ListSubheader {...listSubheaderProps}>
                <HorizontalStack alignItems="center" spacing={1}>
                  <span>{t("tabs.goals.completions-label")}</span>
                  <Badge
                    size="small"
                    color={todosVisible ? "primary" : "default"}
                    variant="outlined"
                    label={`${logs.length} / ${logs.length + todo.length}`}
                    onClick={() => setTodosVisible(!todosVisible)}
                  />
                </HorizontalStack>
              </ListSubheader>
            }
          >
            <Fade in key={targetId}>
              <Box>
                {logs
                  .slice()
                  .sort((a, b) => b.timestamp - a.timestamp)
                  .map((log) => (
                    <ListItem key={log.id} divider>
                      <ListItemIcon>
                        <CheckBoxOutlinedIcon
                          fontSize="small"
                          sx={{ color: "text.primary" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Grid
                            alignItems="center"
                            container
                            spacing={{ xs: 0.5, md: 2 }}
                          >
                            <Grid xs={12} md>
                              <Typography variant="body2" color="text.primary">
                                {log.refTarget?.title}
                              </Typography>
                            </Grid>
                            <Grid xs={12} md="auto">
                              <Typography
                                alignItems="center"
                                color="text.disabled"
                                display="flex"
                                sx={({ spacing }) => ({ gap: spacing(0.5) })}
                                variant="caption"
                              >
                                <DateTime timestamp={log.timestamp} />
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                    </ListItem>
                  ))}
                {todosVisible &&
                  todo.map((item, num) => (
                    <ListItem
                      divider={num < todo.length - 1}
                      key={`${item.id}-${num}`}
                    >
                      <ListItemIcon>
                        <CheckBoxOutlineBlankIcon
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Grid
                            alignItems="center"
                            container
                            spacing={{ xs: 0.5, md: 2 }}
                          >
                            <Grid xs={12} md>
                              <Typography variant="body2" color="text.disabled">
                                {item.refTarget.title}
                              </Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                    </ListItem>
                  ))}
              </Box>
            </Fade>
          </List>
        </Main>
      </Wrap>
    </TabPanel>
  );
};

export default GoalsTab;
